/*
 *  -----------------------------------------------------------------------
 *    <copyright file="visit-tab-grid.data.ts" company="Triumph Research Intelligence">
 *        Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import { IExpandableGridColumnV2 } from '@components/core/expandable/grid-v2/interfaces-v2/grid-props-v2.interface';
import {
    GridColumnDateFilterV2,
    GridColumnMenuActiveFilterV2,
    GridColumnTimeUnitFilter,
    GridColumnEarliestLatestFilter,
} from '@components/core/grid-filters-v2';
import { useGridCells } from '@hooks/index';
import { titleCase } from '@utils/shared';

const { YesNoCell, NumberCell, DateCell, MaxLengthCell, CustomizableCell } = useGridCells();

export const VISIT_TAB_GRID_COLUMNS = (translate: Function): IExpandableGridColumnV2[] => [
    {
        field: 'sequenceNumber',
        label: translate('admin:sequenceNumbers'),
        minWidth: 180,
        id: 'visit-sequence-numbers-visit',
        filter: 'numeric',
        cells: { data: NumberCell },
    },
    {
        label: translate('common:active'),
        field: 'active',
        id: 'visit-active-visit',
        minWidth: 160,
        cells: { data: YesNoCell },
        filterUi: GridColumnMenuActiveFilterV2,
    },
    {
        label: translate('admin:visitType'),
        field: 'type',
        id: 'visit-type-visit',
        minWidth: 170,
        cells: { data: MaxLengthCell(50) },
    },
    {
        label: translate('subject:visitId'),
        field: 'visitId',
        id: 'visit-id-visit',
        minWidth: 160,
        cells: { data: MaxLengthCell(30) },
    },
    {
        label: translate('subject:visitName'),
        field: 'visitName',
        id: 'visit-name-visit',
        minWidth: 190,
        cells: { data: MaxLengthCell(50) },
    },
    {
        field: 'minOccurrences',
        label: translate('admin:numberOccurrencesGridLabel'),
        minWidth: 200,
        id: 'occurrences-visit',
        filter: 'numeric',
        cells: { data: NumberCell },
    },
    {
        label: translate('admin:leadVisitId'),
        field: '_readableLeadVisitId',
        id: 'lead-visit-id-visit',
        minWidth: 190,
        cells: { data: MaxLengthCell(30) },
    },
    {
        field: 'leadPeriod',
        label: translate('admin:leadTime'),
        minWidth: 190,
        id: 'lead-time-visit',
        filter: 'numeric',
        cells: { data: NumberCell },
    },
    {
        label: translate('admin:leadUnit'),
        field: 'leadUnit',
        id: 'lead-unit-visit',
        minWidth: 190,
        cells: { data: CustomizableCell((dataItem: any) => titleCase(dataItem.leadUnit || '')) },
        filterUi: GridColumnTimeUnitFilter,
    },
    {
        field: 'minWinPeriod',
        label: translate('admin:minWinTime'),
        minWidth: 200,
        id: 'min-win-time-visit',
        filter: 'numeric',
        cells: { data: NumberCell },
    },
    {
        field: 'maxWinPeriod',
        label: translate('admin:maxWinTime'),
        minWidth: 200,
        id: 'max-win-time-visit',
        filter: 'numeric',
        cells: { data: NumberCell },
    },
    {
        label: translate('admin:minMaxUnit'),
        field: 'minWinUnit',
        id: 'min-win-unit-visit',
        minWidth: 190,
        cells: { data: CustomizableCell((dataItem: any) => titleCase(dataItem.minWinUnit || '')) },
        filterUi: GridColumnTimeUnitFilter,
    },
    {
        field: '_endDateUsage',
        label: translate('admin:endDateUsage'),
        minWidth: 220,
        id: 'end-date-usage',
        filterUi: GridColumnEarliestLatestFilter,
    },
    {
        field: 'forecastEndDateField',
        label: translate('admin:forecastEndDate'),
        minWidth: 220,
        id: 'forecast-end-date',
    },
    {
        label: translate('ra:createdBy'),
        id: 'visit-createdby',
        field: '_createdByString',
        minWidth: 180,
    },
    {
        label: translate('ra:createdDate'),
        id: 'visit-created',
        minWidth: 200,
        field: 'created',
        cells: { data: DateCell },
        filter: 'date',
        filterUi: GridColumnDateFilterV2,
    },
    {
        label: translate('ra:updatedBy'),
        id: 'visit-updatedby',
        field: '_updatedByString',
        minWidth: 180,
    },
    {
        label: translate('ra:updatedDate'),
        field: 'updated',
        id: 'visit-updated',
        minWidth: 200,
        filter: 'date',
        cells: { data: DateCell },
        filterUi: GridColumnDateFilterV2,
    },
    {
        field: 'recordId',
        label: translate('id'),
        minWidth: 150,
        id: 'visit-id',
        filter: 'numeric',
        cells: { data: NumberCell },
        showByDefault: false,
    },
];

export const ASSOCIATED_VISIT_TAB_GRID_COLUMNS = (translate: Function): IExpandableGridColumnV2[] => [
    {
        field: 'sequenceNumber',
        label: translate('admin:sequenceNumbers'),
        minWidth: 180,
        id: 'visit-sequence-numbers-visit',
        filter: 'numeric',
        cells: { data: NumberCell },
    },
    {
        label: translate('common:active'),
        field: 'active',
        id: 'visit-active-visit',
        minWidth: 160,
        cells: { data: YesNoCell },
        filterUi: GridColumnMenuActiveFilterV2,
    },
    {
        label: translate('admin:visitType'),
        field: 'type',
        id: 'visit-type-visit',
        minWidth: 170,
        cells: { data: MaxLengthCell(50) },
    },
    {
        label: translate('subject:visitId'),
        field: 'visitId',
        id: 'visit-id-visit',
        minWidth: 160,
        cells: { data: MaxLengthCell(30) },
    },
    {
        label: translate('subject:visitName'),
        field: 'visitName',
        id: 'visit-name-visit',
        minWidth: 190,
        cells: { data: MaxLengthCell(50) },
    },
    {
        label: translate('admin:leadVisitId'),
        field: '_readableLeadVisitId',
        id: 'lead-visit-id-visit',
        minWidth: 190,
        cells: { data: MaxLengthCell(30) },
    },
    {
        field: 'leadPeriod',
        label: translate('admin:leadTime'),
        minWidth: 190,
        id: 'lead-time-visit',
        filter: 'numeric',
        cells: { data: NumberCell },
    },
    {
        label: translate('admin:leadUnit'),
        field: 'leadUnit',
        id: 'lead-unit-visit',
        minWidth: 190,
        cells: { data: CustomizableCell((dataItem: any) => titleCase(dataItem.leadUnit || '')) },
        filterUi: GridColumnTimeUnitFilter,
    },
    {
        field: 'minWinPeriod',
        label: translate('admin:minWinTime'),
        minWidth: 200,
        id: 'min-win-time-visit',
        filter: 'numeric',
        cells: { data: NumberCell },
    },
    {
        field: 'maxWinPeriod',
        label: translate('admin:maxWinTime'),
        minWidth: 200,
        id: 'max-win-time-visit',
        filter: 'numeric',
        cells: { data: NumberCell },
    },
    {
        label: translate('admin:minMaxUnit'),
        field: 'minWinUnit',
        id: 'min-win-unit-visit',
        minWidth: 190,
        cells: { data: CustomizableCell((dataItem: any) => titleCase(dataItem.minWinUnit || '')) },
        filterUi: GridColumnTimeUnitFilter,
    },
    {
        field: 'minOccurrences',
        label: translate('admin:numberOccurrencesGridLabel'),
        minWidth: 200,
        id: 'occurrences-visit',
        filter: 'numeric',
        cells: { data: NumberCell },
    },
    {
        label: translate('ra:createdBy'),
        id: 'visit-createdby',
        field: '_createdByString',
        minWidth: 180,
    },
    {
        label: translate('ra:createdDate'),
        id: 'visit-created',
        minWidth: 200,
        field: 'created',
        cells: { data: DateCell },
        filter: 'date',
        filterUi: GridColumnDateFilterV2,
    },
    {
        label: translate('ra:updatedBy'),
        id: 'visit-updatedby',
        field: '_updatedByString',
        minWidth: 180,
    },
    {
        label: translate('ra:updatedDate'),
        field: 'updated',
        id: 'visit-updated',
        minWidth: 200,
        filter: 'date',
        cells: { data: DateCell },
        filterUi: GridColumnDateFilterV2,
    },
    {
        field: 'recordId',
        label: translate('id'),
        minWidth: 150,
        id: 'visit-id',
        filter: 'numeric',
        cells: { data: NumberCell },
        showByDefault: false,
    },
];
